import React, { useState, useEffect, useCallback } from 'react';
import { getHostelAdmissionsPortal, getHostelRequestsPortal } from '../../api';
import { FiAlertTriangle, FiUsers, FiCheckCircle, FiTrendingUp, FiDollarSign, FiClock, FiClipboard, FiPieChart, FiUser } from 'react-icons/fi';

// ── SVG Donut Chart ─────────────────────────────────────────────────
function DonutChart({ active, exited, size = 120 }) {
  const total = active + exited || 1;
  const r = 44, cx = 60, cy = 60, circ = 2 * Math.PI * r;
  const activeArc = (active / total) * circ;
  const exitedArc = (exited / total) * circ;
  return (
    <svg width={size} height={size} viewBox="0 0 120 120">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="14" />
      {exited > 0 && (
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#6366f1" strokeWidth="14"
          strokeDasharray={`${exitedArc} ${circ}`} strokeDashoffset={-activeArc}
          strokeLinecap="round" style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }} />
      )}
      {active > 0 && (
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#10b981" strokeWidth="14"
          strokeDasharray={`${activeArc} ${circ}`} strokeDashoffset="0"
          strokeLinecap="round" style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }} />
      )}
      <text x={cx} y={cy - 6} textAnchor="middle" fill="#e2e8f0" fontSize="18" fontWeight="700">{active + exited}</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fill="#64748b" fontSize="9">TOTAL</text>
    </svg>
  );
}

// ── Horizontal Bar ───────────────────────────────────────────────────
function BarRow({ label, value, max, color, prefix = 'Rs. ' }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
        <span style={{ fontSize: '12px', color: '#94a3b8' }}>{label}</span>
        <span style={{ fontSize: '12px', fontWeight: '700', color }}>{prefix}{Number(value).toLocaleString()}</span>
      </div>
      <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '6px', height: '8px', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: '6px', transition: 'width 0.8s ease' }} />
      </div>
    </div>
  );
}

export default function HostelDashboard({ hostel }) {
  const [stats, setStats] = useState({ residents: 0, active: 0, exited: 0, totalDues: 0, totalPaid: 0, balance: 0, pendingRequests: 0, recentAdmissions: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, rRes] = await Promise.all([
        getHostelAdmissionsPortal(),
        getHostelRequestsPortal({ status: 'pending' })
      ]);
      const adms = aRes.data;
      const totalDues = adms.reduce((s, a) => s + (Number(a.total_dues) || 0), 0);
      const totalPaid = adms.reduce((s, a) => s + (Number(a.total_paid) || 0), 0);
      const active = adms.filter(a => a.status === 'active').length;
      const exited = adms.filter(a => a.status === 'exited').length;
      setStats({
        residents: adms.length,
        active,
        exited,
        totalDues,
        totalPaid,
        balance: totalDues - totalPaid,
        pendingRequests: rRes.data.length,
        recentAdmissions: adms.slice(0, 5)
      });
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const card = (label, value, icon, color, sub) => (
    <div style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${color}28`, borderRadius: '14px', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '14px' }}>
      <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: `${color}18`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</div>
      <div>
        <div style={{ fontSize: '22px', fontWeight: '800', color, lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px', fontWeight: '500' }}>{label}</div>
        {sub && <div style={{ fontSize: '10px', color: '#475569', marginTop: '1px' }}>{sub}</div>}
      </div>
    </div>
  );

  const maxBar = Math.max(stats.totalDues, 1);

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ color: '#fff', margin: '0 0 4px', fontSize: '22px' }}>
          Welcome, <span style={{ color: '#f5bc1f' }}>{hostel?.name || 'Hostel'}</span>
        </h2>
        <p style={{ color: '#64748b', margin: 0, fontSize: '13px' }}>
          In-charge: {hostel?.incharge_name} | {new Date().toLocaleDateString('en-PK', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ width: '36px', height: '36px', border: '3px solid rgba(255,255,255,0.08)', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }}></div>
          <div style={{ color: '#64748b', fontSize: '13px' }}>Loading dashboard…</div>
        </div>
      ) : (
        <>
          {/* Pending alert */}
          {stats.pendingRequests > 0 && (
            <div style={{ marginBottom: '20px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '10px', padding: '12px 16px', color: '#fcd34d', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FiAlertTriangle size={15} /> <span>You have <strong>{stats.pendingRequests}</strong> pending request(s) awaiting admin review.</span>
            </div>
          )}

          {/* Stat Cards Row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', marginBottom: '24px' }}>
            {card('Total Residents', stats.residents, <FiUsers size={20} />, '#3b82f6')}
            {card('Active Residents', stats.active, <FiCheckCircle size={20} />, '#10b981', `${stats.exited} exited`)}
            {card('Total Dues', `Rs. ${stats.totalDues.toLocaleString()}`, <FiTrendingUp size={20} />, '#f59e0b')}
            {card('Total Received', `Rs. ${stats.totalPaid.toLocaleString()}`, <FiDollarSign size={20} />, '#10b981')}
            {card('Pending Balance', `Rs. ${stats.balance.toLocaleString()}`, <FiClock size={20} />, stats.balance > 0 ? '#ef4444' : '#10b981')}
            {card('Pending Requests', stats.pendingRequests, <FiClipboard size={20} />, stats.pendingRequests > 0 ? '#f59e0b' : '#6b7280')}
          </div>

          {/* Charts Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>

            {/* Financial Bar Chart */}
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '14px', padding: '20px' }}>
              <h4 style={{ color: '#e2e8f0', margin: '0 0 18px', fontSize: '13px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '8px' }}><FiTrendingUp size={14} /> Financial Overview</h4>
              {stats.residents === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#475569', fontSize: '13px' }}>No financial data yet</div>
              ) : (
                <>
                  <BarRow label="Total Dues" value={stats.totalDues} max={maxBar} color="#f59e0b" />
                  <BarRow label="Amount Paid" value={stats.totalPaid} max={maxBar} color="#10b981" />
                  <BarRow label="Pending Balance" value={stats.balance} max={maxBar} color={stats.balance > 0 ? '#ef4444' : '#10b981'} />
                  <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#64748b' }}>
                    <span>Recovery: <strong style={{ color: '#10b981' }}>{stats.totalDues > 0 ? Math.round((stats.totalPaid / stats.totalDues) * 100) : 0}%</strong></span>
                    <span>Outstanding: <strong style={{ color: '#f59e0b' }}>Rs. {stats.balance.toLocaleString()}</strong></span>
                  </div>
                </>
              )}
            </div>

            {/* Occupancy Donut */}
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '14px', padding: '20px' }}>
              <h4 style={{ color: '#e2e8f0', margin: '0 0 14px', fontSize: '13px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '8px' }}><FiPieChart size={14} /> Occupancy Status</h4>
              {stats.residents === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#475569', fontSize: '13px' }}>No residents added yet</div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', justifyContent: 'center' }}>
                  <DonutChart active={stats.active} exited={stats.exited} size={120} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#10b981', flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#10b981' }}>{stats.active}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>Active</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#6366f1', flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#6366f1' }}>{stats.exited}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>Exited</div>
                      </div>
                    </div>
                    {hostel?.total_rooms > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#475569', flexShrink: 0 }} />
                        <div>
                          <div style={{ fontSize: '16px', fontWeight: '700', color: '#94a3b8' }}>{Math.max(0, hostel.total_rooms - stats.active)}</div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>Vacant</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recent Admissions */}
          {stats.recentAdmissions.length > 0 && (
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '14px', padding: '20px' }}>
              <h4 style={{ color: '#e2e8f0', margin: '0 0 14px', fontSize: '13px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '8px' }}><FiClock size={14} /> Recent Residents</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {stats.recentAdmissions.map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: a.status === 'active' ? 'rgba(16,185,129,0.15)' : 'rgba(99,102,241,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>
                        <FiUser size={14} color={a.status === 'active' ? '#10b981' : '#a5b4fc'} />
                      </div>
                      <div>
                        <div style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: '600' }}>{a.full_name}</div>
                        <div style={{ color: '#64748b', fontSize: '11px' }}>
                          {a.date_of_admission ? new Date(a.date_of_admission).toLocaleDateString('en-PK') : '—'}
                          {a.contact_no && ` | ${a.contact_no}`}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: (a.balance || 0) > 0 ? '#ef4444' : '#10b981' }}>
                        Rs. {Number(a.balance || 0).toLocaleString()}
                      </span>
                      <span style={{ padding: '2px 8px', borderRadius: '20px', fontSize: '10px', fontWeight: '600', background: a.status === 'active' ? 'rgba(16,185,129,0.15)' : 'rgba(99,102,241,0.15)', color: a.status === 'active' ? '#34d399' : '#a5b4fc' }}>
                        {a.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
